/**
 * ViewPage — limited read-only map portal accessed via a one-time token.
 *
 * URL:   /view?t={viewToken}
 * Flow:
 *   1. Extract ?t= from URL
 *   2. POST /ext/v1/redeem  →  { accessToken, expiresAt, vehicles }
 *   3. Build an axios instance carrying the VIEW JWT as Bearer
 *   4. Fetch vehicle list (filtered server-side to allowed units)
 *   5. Show live map; poll every 30 s
 *   6. Click a vehicle → date picker → history route on map
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, IconButton, Tooltip, Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import MapWrapperComp from "../google-map/mapWrapper";
import { calcIcon } from "../google-map/svgFiles";
import axiosBase from "../../api/axios"; // re-use the base URL logic

// ─── helpers ──────────────────────────────────────────────────────────────────

function buildViewAxios(jwt) {
  return axios.create({
    baseURL: axiosBase.defaults.baseURL,
    headers: {
      "Content-Type": "application/json",
      Authorization:  `Bearer ${jwt}`,
    },
    withCredentials: false, // VIEW sessions have no cookie
  });
}

function toIsoLocal(date) {
  // e.g. 2025-06-01T00:00:00
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`;
}
function toIsoLocalEnd(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T23:59:59`;
}

// ─── component ────────────────────────────────────────────────────────────────

export default function ViewPage() {
  const [phase,       setPhase]      = useState("loading");  // loading | error | live | history
  const [errorMsg,    setErrorMsg]   = useState("");
  const [markers,     setMarkers]    = useState([]);
  const [histMarkers, setHistMarkers]= useState([]);
  const [histVehicle, setHistVehicle]= useState(null);  // { unitNum, reg }
  const [histDate,    setHistDate]   = useState(new Date());
  const [histLoading, setHistLoading]= useState(false);
  const [histOpen,    setHistOpen]   = useState(false);
  const [zoomTarget,  setZoomTarget] = useState(null);  // { lat, lng } to pan map to

  const jwtRef       = useRef(null);
  const axRef        = useRef(null);   // view axios instance
  const pollRef      = useRef(null);   // interval handle
  const countRef     = useRef(0);      // vehicle count for banner
  const zoomToRef    = useRef(null);   // unit number to zoom to on first load
  const hasZoomedRef = useRef(false);  // only auto-zoom once

  // Extract token from URL
  const token = new URLSearchParams(window.location.search).get("t");

  // ── token redemption ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setErrorMsg("No view token in URL. Ask your provider for a new link.");
      setPhase("error");
      return;
    }

    axiosBase.post("/ext/v1/redeem", { token })
      .then(({ data }) => {
        jwtRef.current   = data.accessToken;
        axRef.current    = buildViewAxios(data.accessToken);
        countRef.current = (data.vehicles || []).length;
        zoomToRef.current = data.zoomToUnit || data.ZoomToUnit || null;
        loadVehicles();
      })
      .catch((err) => {
        setErrorMsg(
          err.response?.data?.error ||
          "Token is invalid, already used, or expired. Ask your provider for a new link."
        );
        setPhase("error");
      });

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── vehicle loader / live polling ───────────────────────────────────────────
  const loadVehicles = useCallback(() => {
    if (!axRef.current) return;
    axRef.current.post("/users/GetVehicles")
      .then(({ data }) => {
        const today = new Date();
        const marks = (Array.isArray(data) ? data : [])
          .filter(v => v.position && v.position.Ops !== "LGL")
          .map(v => ({
            position: { lat: v.position.Latitude, lng: v.position.Longitude },
            reg:      v.reg,
            unitNum:  v.unitNum,
            title:    v.reg,
            heading:  v.position.Heading,
            ignOn:    v.position.Ignition === "On",
            infoContent: {
              reg:         v.reg,
              lat:         v.position.Latitude,
              lng:         v.position.Longitude,
              speed:       v.position.kph,
              ignitionOn:  v.position.Ignition === "On",
              updateDate:  v.position.UpdateTime && new Date(v.position.UpdateTime),
              carMakeModel:v.carMakeModel,
            },
            icon: calcIcon(
              false, undefined,
              v.position.Heading,
              v.position.Ignition === "On",
              undefined,
              v.position.UpdateTime &&
                (today - new Date(v.position.UpdateTime)) / 3600000 > 96,
              v.carMakeModel
            ),
            // attach for the history dialog
            _unitNum: v.unitNum,
            _reg:     v.reg,
          }));

        setMarkers(marks);
        setPhase("live");

        // Auto-zoom to specified unit on first load only
        if (zoomToRef.current && !hasZoomedRef.current) {
          const target = marks.find(
            m => m._unitNum === zoomToRef.current || m._unitNum?.toString() === zoomToRef.current?.toString()
          );
          if (target) {
            setZoomTarget({ lat: target.position.lat, lng: target.position.lng });
            hasZoomedRef.current = true;
          }
        }

        // Start polling if not already running
        if (!pollRef.current) {
          pollRef.current = setInterval(loadVehicles, 30_000);
        }
      })
      .catch(() => {
        // silently skip failed polls; initial failure leaves phase="loading"
      });
  }, []);

  // ── history loader ──────────────────────────────────────────────────────────
  const loadHistory = (unitNum, reg, date) => {
    if (!axRef.current) return;
    setHistLoading(true);
    axRef.current.post("/users/GetTripsHistory", {
      unitNum,
      startDate: toIsoLocal(date),
      endDate:   toIsoLocalEnd(date),
      isBeacon:  false,
    })
      .then(({ data }) => {
        const positions = Array.isArray(data) ? data : JSON.parse(data || "[]");
        const hMarks = positions.map((pos, i) => {
          if (i === 0) return {
            position: { lat: Number(pos.lat), lng: Number(pos.long) },
            icon: calcIcon(true, "start"),
          };
          if (i === positions.length - 1) return {
            position: { lat: Number(pos.lat), lng: Number(pos.long) },
            icon: calcIcon(true, "end"),
          };
          return {
            position: { lat: Number(pos.lat), lng: Number(pos.long) },
            heading: Number(pos.bearing || 0),
            icon: calcIcon(true, undefined, Number(pos.bearing || 0)),
          };
        });
        setHistMarkers(hMarks);
        setPhase("history");
        setHistOpen(false);
      })
      .catch(() => {})
      .finally(() => setHistLoading(false));
  };

  // ── render: loading ─────────────────────────────────────────────────────────
  if (phase === "loading") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center",
                 justifyContent: "center", height: "100vh", gap: 2 }}>
        <CircularProgress sx={{ color: "#00bcd4" }} />
        <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
          Connecting to view portal…
        </Typography>
      </Box>
    );
  }

  // ── render: error ───────────────────────────────────────────────────────────
  if (phase === "error") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center",
                 justifyContent: "center", height: "100vh", gap: 2, p: 3 }}>
        <FontAwesomeIcon icon="triangle-exclamation" style={{ fontSize: 40, color: "#e53935" }} />
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, color: "#333" }}>
          View Portal Unavailable
        </Typography>
        <Alert severity="error" sx={{ maxWidth: 420, fontSize: "0.83rem" }}>{errorMsg}</Alert>
        <Button variant="contained" href="/login"
          sx={{ mt: 1, backgroundColor: "#00bcd4", "&:hover": { backgroundColor: "#00838f" },
                textTransform: "none" }}>
          Login to Full System
        </Button>
      </Box>
    );
  }

  // ── render: live / history ──────────────────────────────────────────────────
  const displayMarkers = phase === "history" ? histMarkers : markers;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>

      {/* ── Banner ──────────────────────────────────────────────────────── */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        px: 2, py: 0.75, flexShrink: 0, zIndex: 1200,
        background: "linear-gradient(90deg,#00838f,#00bcd4)",
        color: "#fff",
      }}>
        <FontAwesomeIcon icon="eye" style={{ fontSize: 13 }} />
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
          Limited View
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
          — {countRef.current} vehicle{countRef.current !== 1 ? "s" : ""} —
          read-only snapshot
        </Typography>

        {phase === "history" && (
          <Button size="small" variant="outlined"
            onClick={() => { setPhase("live"); setHistMarkers([]); setHistVehicle(null); }}
            sx={{ ml: 1, color: "#fff", borderColor: "rgba(255,255,255,0.5)", fontSize: "0.73rem",
                  textTransform: "none", py: 0.25,
                  "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}
          >
            ← Back to Live
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {/* History button — visible when a vehicle is selected */}
        {phase === "live" && (
          <Tooltip title="View history for a vehicle">
            <Button size="small" variant="outlined"
              onClick={() => setHistOpen(true)}
              startIcon={<FontAwesomeIcon icon="fa-clock-rotate-left" style={{ fontSize: 11 }} />}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", fontSize: "0.73rem",
                    textTransform: "none", py: 0.25,
                    "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}
            >
              View History
            </Button>
          </Tooltip>
        )}

        <Button size="small" variant="outlined" href="/login"
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", fontSize: "0.73rem",
                textTransform: "none", py: 0.25,
                "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}
        >
          Login for Full Access →
        </Button>
      </Box>

      {/* ── Map ─────────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, position: "relative" }}>
        <MapWrapperComp
          markers={displayMarkers}
          mapLiveView={phase === "live"}
          displayPath={phase === "history"}
          center={zoomTarget}
        />
      </Box>

      {/* ── History picker dialog ────────────────────────────────────────── */}
      <Dialog open={histOpen} onClose={() => setHistOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 340 } }}>
        <DialogTitle sx={{
          background: "linear-gradient(135deg,#00838f,#00bcd4)",
          color: "#fff", py: 1.5, px: 2.5, fontSize: "0.95rem", fontWeight: 600,
          display: "flex", alignItems: "center", gap: 1,
        }}>
          <FontAwesomeIcon icon="fa-clock-rotate-left" style={{ fontSize: 14 }} />
          View History
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setHistOpen(false)} sx={{ color: "#fff", p: 0.5 }}>
            <FontAwesomeIcon icon="xmark" style={{ fontSize: 14 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 2.5 }}>
          <Typography sx={{ fontSize: "0.82rem", color: "#555", mb: 1.5 }}>
            Select a vehicle and date to view its route.
          </Typography>

          {/* Vehicle selector */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
            {markers.map(m => (
              <Box key={m._unitNum}
                onClick={() => setHistVehicle({ unitNum: m._unitNum, reg: m._reg })}
                sx={{
                  px: 1.5, py: 0.5, borderRadius: "20px", cursor: "pointer",
                  fontSize: "0.8rem", fontWeight: 500,
                  border: histVehicle?.unitNum === m._unitNum
                    ? "1.5px solid #00bcd4" : "1.5px solid #ddd",
                  bgcolor: histVehicle?.unitNum === m._unitNum
                    ? "rgba(0,188,212,0.1)" : "transparent",
                  color: histVehicle?.unitNum === m._unitNum ? "#00838f" : "#555",
                  "&:hover": { borderColor: "#00bcd4", bgcolor: "rgba(0,188,212,0.06)" },
                }}
              >
                {m._reg}
              </Box>
            ))}
          </Box>

          {/* Date picker */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              label="Date"
              value={histDate}
              onChange={(d) => d && setHistDate(d)}
              maxDate={new Date()}
              renderInput={(params) => (
                <Box sx={{ "& input": { fontSize: "0.82rem" } }}>
                  {params.inputProps && (
                    <input {...params.inputProps}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 6,
                               border: "1px solid #ddd", fontSize: "0.82rem" }} />
                  )}
                </Box>
              )}
            />
          </LocalizationProvider>

          <Button variant="contained" fullWidth
            disabled={!histVehicle || histLoading}
            onClick={() => loadHistory(histVehicle.unitNum, histVehicle.reg, histDate)}
            sx={{ mt: 2, backgroundColor: "#00bcd4", "&:hover": { backgroundColor: "#00838f" },
                  textTransform: "none", fontSize: "0.83rem" }}
          >
            {histLoading ? "Loading…" : "Show Route"}
          </Button>
        </DialogContent>
      </Dialog>

    </Box>
  );
}
